import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Users } from "lucide-react";
import { useLets } from "@/hooks/useLets";
import { AgentLayout } from "@/components/agent/AgentLayout";
import { SetupChecklist } from "@/components/agent/SetupChecklist";
import { DecisionTag, EmptyState, FraudTag, ListingTag, Loading, PageHeader, ProgressBar, ProgressTag, SecondaryButton, VerdictTag } from "@/components/agent/ui";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PropertyImage } from "@/components/agent/PropertyImage";
import { ApplicantMark, DocStamps } from "@/components/agent/ApplicantMark";
import { gbp, longDate } from "@/lib/format";

/** With one property picked, its address and photo are already in the summary above, so the card leaves them out. */
const ApplicantCard = ({ a, i, showProperty }) => {
  const items = a.items_total || 0;
  return (
    <Link to={`/lets/${a.let_.id}/applicants/${a.id}`} className="group flex flex-col gap-4 rounded-xl border bg-background p-4 transition-colors hover:bg-panel" data-testid={`applicant-card-${i}`}>
      <div className="flex items-start gap-3">
        <ApplicantMark a={a} testid={`applicant-mark-${i}`} />
        <div className="min-w-0 flex-1 pt-1">
          <p className="text-sm font-medium" data-testid={`applicant-label-${i}`}>{a.label}</p>
          {showProperty ? (
            <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground" data-testid={`applicant-property-${i}`}>
              <span className="size-4 shrink-0 overflow-hidden rounded-[4px]"><PropertyImage src={a.let_.image_data} /></span>
              <span className="truncate">{a.let_.address}</span>
            </div>
          ) : (
            <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">{items ? `${a.items_done || 0} of ${items} ${items === 1 ? "document" : "documents"}` : "No documents yet"}</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 pt-1">
          {a.fraud_flag ? <FraudTag testid={`applicant-fraud-${i}`} /> : <VerdictTag status={a.status} outcome={a.outcome} testid={`applicant-verdict-${i}`} />}
          <DecisionTag decision={a.decision} testid={`applicant-decision-${i}`} />
        </div>
      </div>
      <div className="flex items-center justify-between gap-3">
        <ProgressTag status={a.status} testid={`applicant-status-${i}`} />
        {a.items?.length ? <DocStamps items={a.items} testid={`applicant-stamps-${i}`} /> : <span className="text-xs text-muted-foreground">–</span>}
      </div>
    </Link>
  );
};

const PropertySummary = ({ let_ }) => {
  const total = let_.applicants.length;
  const done = let_.applicants.filter((a) => a.status === "documents_complete").length;
  return (
    <div className="flex items-center gap-4 rounded-xl border bg-background p-3" data-testid="referencing-progress">
      <span className="h-14 w-20 shrink-0 overflow-hidden rounded-lg"><PropertyImage src={let_.image_data} /></span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2"><p className="truncate text-sm font-medium">{let_.address}</p><ListingTag status={let_.listing_status} /></div>
        <p className="text-xs text-muted-foreground">{gbp(let_.monthly_rent)} a month · move in {longDate(let_.move_in_date)}</p>
      </div>
      <div className="hidden w-52 sm:block">
        <div className="mb-1.5 flex items-center justify-between text-xs">
          <span className="font-medium">Referencing</span>
          <span className="text-muted-foreground" data-testid="referencing-count">{done} of {total} complete</span>
        </div>
        <ProgressBar value={done} total={total} />
      </div>
    </div>
  );
};

export default function Home() {
  const { lets, error, load } = useLets();
  const [created, setCreated] = useState(null);
  const [filter, setFilter] = useState("all");
  const setup = lets && (lets.length === 0 || created);
  const selected = lets?.find((l) => l.id === filter);
  const cards = useMemo(
    () => (lets || []).filter((l) => filter === "all" || l.id === filter).flatMap((l) => l.applicants.map((a) => ({ ...a, let_: l }))),
    [lets, filter],
  );

  let body;
  if (error) body = <EmptyState title="Couldn't load applicants" text={error} action={<SecondaryButton onClick={load} testid="home-retry">Try again</SecondaryButton>} testid="home-error" />;
  else if (!lets) body = <Loading testid="home-loading" />;
  else if (setup) body = <SetupChecklist created={created} onCreated={setCreated} onFinish={() => { setCreated(null); load(); }} />;
  else if (!cards.length) body = <EmptyState icon={Users} title="No applicants yet" text="Applicants appear here as soon as they open your apply link." action={<SecondaryButton to="/properties" testid="empty-go-properties">View properties</SecondaryButton>} testid="applicants-empty" />;
  else
    body = (
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="applicants-grid">
        {cards.map((a, i) => <ApplicantCard key={a.id} a={a} i={i} showProperty={!selected} />)}
      </div>
    );

  const picker = !setup && lets && (
    <Select value={filter} onValueChange={setFilter}>
      <SelectTrigger size="input" className="w-72 max-w-full" data-testid="property-filter-select"><SelectValue /></SelectTrigger>
      <SelectContent align="end">
        <SelectItem value="all" data-testid="property-filter-all">All properties</SelectItem>
        <SelectSeparator />
        {lets.map((l, i) => <SelectItem key={l.id} value={l.id} data-testid={`property-filter-option-${i}`}>{l.address}</SelectItem>)}
      </SelectContent>
    </Select>
  );


  return (
    <AgentLayout wide>
      <PageHeader title={setup ? "Set up Landin" : "Applicants"}
        description={setup ? "Three steps and your first property is taking applications." : "Everyone applying to your properties, checked against the same criteria."} action={picker} />
      {selected && <PropertySummary let_={selected} />}
      {body}
    </AgentLayout>
  );
}
